/**
 * ClientMeasurementsPanel — histórico de medidas corporales de UN cliente.
 *
 * Se usa como step 4 del ClientFormModal (sólo en modo edit, cuando el cliente
 * ya tiene id) y puede reusarse en cualquier vista de detalle del cliente.
 *
 * UI:
 *   - Header: "Medidas corporales" + botón "+ Nueva medición".
 *   - Sin medidas: EmptyState con CTA.
 *   - Con medidas: tabla de progreso comparativa estilo spreadsheet.
 *       · Filas   = campos de medida (peso, % grasa, perímetros…).
 *       · Columnas = las últimas 4 tomas (fecha en el header).
 *       · Primera columna (labels) sticky; scroll horizontal en las tomas.
 *       · Cada celda muestra el valor + delta vs la toma anterior (gris, sin
 *         colorear bien/mal — "subir cintura" es malo pero "subir brazo" es
 *         bueno y no codificamos esa lógica aún).
 *       · Cada columna tiene botón ✕ para eliminar la toma (con confirm).
 *   - Modal "Nueva medición": form con todos los campos + notas.
 *
 * Mobile-first: a 375px la tabla scrollea horizontal sin romper layout.
 */
import { useCallback, useEffect, useState, type ChangeEvent } from "react";
import { Icon } from "../../lib/Icon";
import { EmptyState } from "../../lib/components";
import { useToast } from "../../lib/toast";
import {
  createMeasurement,
  deleteMeasurement,
  listMeasurements,
  MEASUREMENT_FIELDS,
  type ClientMeasurement,
  type MeasurementInput,
} from "../../api/measurements";

interface ClientMeasurementsPanelProps {
  clientId: number;
  /** Notifica al padre cada vez que el histórico cambia (crear/eliminar).
   *  Útil para que el ClientFormModal sepa que hubo actividad. */
  onChanged?: () => void;
}

/** Cuántas tomas mostrar en la tabla comparativa (las más recientes). */
const MAX_COLUMNS = 4;

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Formatea una fecha YYYY-MM-DD a algo corto y legible (es-CO). */
function fmtDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}

/** Delta numérico con signo explícito; "−" tipográfico para negativos. */
function fmtDelta(curr: number, prev: number): string {
  const d = curr - prev;
  if (Math.abs(d) < 0.001) return "=";
  const rounded = Math.round(d * 10) / 10;
  return rounded > 0 ? `+${rounded}` : `−${Math.abs(rounded)}`;
}

export function ClientMeasurementsPanel({ clientId, onChanged }: ClientMeasurementsPanelProps) {
  const toast = useToast();
  const [rows, setRows] = useState<ClientMeasurement[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await listMeasurements(clientId);
      setRows(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = useCallback(
    async (m: ClientMeasurement) => {
      if (!confirm(`¿Eliminar la toma del ${fmtDate(m.measured_at)}?`)) return;
      setDeletingId(m.id);
      try {
        await deleteMeasurement(clientId, m.id);
        toast("Medición eliminada");
        await load();
        onChanged?.();
      } catch (e) {
        toast("No pude eliminar: " + (e instanceof Error ? e.message : ""));
      } finally {
        setDeletingId(null);
      }
    },
    [clientId, load, onChanged, toast],
  );

  const handleCreated = useCallback(async () => {
    setShowModal(false);
    await load();
    onChanged?.();
  }, [load, onChanged]);

  // Las 4 tomas más recientes (el backend ya devuelve más reciente primero).
  const columns = (rows ?? []).slice(0, MAX_COLUMNS);

  return (
    <div className="al-meas-cli">
      <div className="al-meas-cli-head">
        <div>
          <div className="al-meas-cli-title">Medidas corporales</div>
          <div className="muted tiny">
            {rows && rows.length > 0
              ? `${rows.length} toma${rows.length === 1 ? "" : "s"} registrada${rows.length === 1 ? "" : "s"}`
              : "Histórico de perímetros y composición"}
          </div>
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowModal(true)}
          type="button"
        >
          <Icon name="plus" size={14} /> Nueva medición
        </button>
      </div>

      {loading && !rows && <div className="muted tiny al-meas-cli-state">Cargando medidas…</div>}

      {err && !rows && (
        <div className="al-meas-cli-state">
          <EmptyState
            icon="sparkle"
            title="No pude cargar las medidas"
            body={err}
            action={
              <button className="btn btn-secondary btn-sm" onClick={load} type="button">
                Reintentar
              </button>
            }
          />
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="al-meas-cli-state">
          <EmptyState
            icon="sparkle"
            title="Aún no hay medidas registradas"
            body="Registra una primera toma para empezar a seguir el progreso de tu cliente: peso, % grasa y perímetros."
            action={
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowModal(true)}
                type="button"
              >
                <Icon name="plus" size={14} /> Registrar primera medición
              </button>
            }
          />
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="al-meas-cli-tablewrap">
          <table className="al-meas-cli-table">
            <thead>
              <tr>
                <th className="al-meas-cli-th-field">Medida</th>
                {columns.map((m) => (
                  <th key={m.id} className="al-meas-cli-th-col">
                    <div className="al-meas-cli-colhead">
                      <span className="al-meas-cli-coldate">{fmtDate(m.measured_at)}</span>
                      <button
                        className="al-meas-cli-del"
                        type="button"
                        onClick={() => handleDelete(m)}
                        disabled={deletingId === m.id}
                        title="Eliminar esta toma"
                        aria-label={`Eliminar toma del ${fmtDate(m.measured_at)}`}
                      >
                        <Icon name="x" size={11} />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MEASUREMENT_FIELDS.map((f) => {
                // Mostramos la fila sólo si al menos una toma visible tiene valor.
                const hasAny = columns.some((m) => {
                  const v = m[f.key as keyof ClientMeasurement];
                  return v != null;
                });
                if (!hasAny) return null;
                return (
                  <tr key={f.key as string}>
                    <th className="al-meas-cli-td-field" scope="row">
                      {f.label}
                      <span className="al-meas-cli-unit">{f.unit}</span>
                    </th>
                    {columns.map((m, ci) => {
                      const raw = m[f.key as keyof ClientMeasurement];
                      const val = typeof raw === "number" ? raw : null;
                      // La toma "anterior" en orden cronológico es la columna a la
                      // derecha (columns está ordenado de más reciente a más antigua).
                      const prevCol = columns[ci + 1];
                      const prevRaw = prevCol
                        ? prevCol[f.key as keyof ClientMeasurement]
                        : undefined;
                      const prevVal = typeof prevRaw === "number" ? prevRaw : null;
                      return (
                        <td key={m.id} className="al-meas-cli-cell">
                          {val != null ? (
                            <>
                              <span className="al-meas-cli-val">{val}</span>
                              {prevVal != null && (
                                <span className="al-meas-cli-delta">
                                  {fmtDelta(val, prevVal)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="al-meas-cli-empty">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > MAX_COLUMNS && (
            <div className="muted tiny al-meas-cli-foot">
              Mostrando las {MAX_COLUMNS} tomas más recientes de {rows.length}.
            </div>
          )}
        </div>
      )}

      {showModal && (
        <NewMeasurementModal
          clientId={clientId}
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}

// ---------------- Modal "Nueva medición" ----------------

interface NewMeasurementModalProps {
  clientId: number;
  onClose: () => void;
  onCreated: () => void;
}

/** Estado del form: todos los campos como strings (inputs HTML). */
type MeasForm = Record<string, string> & { measured_at: string; notes: string };

function emptyForm(): MeasForm {
  const f: MeasForm = { measured_at: todayISO(), notes: "" };
  for (const fld of MEASUREMENT_FIELDS) f[fld.key as string] = "";
  return f;
}

function num(s: string): number | null {
  if (!s.trim()) return null;
  const n = parseFloat(s.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function NewMeasurementModal({ clientId, onClose, onCreated }: NewMeasurementModalProps) {
  const toast = useToast();
  const [form, setForm] = useState<MeasForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const setField = (k: string, v: string) => setForm((s) => ({ ...s, [k]: v }));

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setErrMsg(null);
    try {
      const payload: MeasurementInput = {};
      if (form.measured_at) payload.measured_at = form.measured_at;
      for (const fld of MEASUREMENT_FIELDS) {
        const v = num(form[fld.key as string]);
        if (v != null) (payload as Record<string, unknown>)[fld.key as string] = v;
      }
      if (form.notes.trim()) payload.notes = form.notes.trim();
      await createMeasurement(clientId, payload);
      toast("Medición registrada");
      onCreated();
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="al-cli-modal-backdrop" onClick={onClose}>
      <div
        className="al-cli-modal al-meas-cli-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Nueva medición"
      >
        <div className="al-cli-modal-head">
          <div>
            <div className="al-cli-modal-title">Nueva medición</div>
            <div className="muted tiny">Registra una toma de perímetros y composición</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} title="Cerrar" type="button">
            <Icon name="x" size={14} />
          </button>
        </div>

        {errMsg && (
          <div className="al-cli-modal-banner al-cli-modal-banner--err">
            <b>No se pudo guardar.</b> <span className="muted">{errMsg}</span>
          </div>
        )}

        <div className="al-cli-modal-body">
          <div className="al-cli-form">
            <div className="field">
              <label>Fecha de la toma</label>
              <input
                type="date"
                value={form.measured_at}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setField("measured_at", e.target.value)
                }
              />
              <span className="hint">Si la dejas vacía se usa la fecha de hoy.</span>
            </div>

            <div className="al-meas-cli-formgrid">
              {MEASUREMENT_FIELDS.map((f) => (
                <div className="field al-meas-cli-formfield" key={f.key as string}>
                  <label>{f.label}</label>
                  <div className="al-meas-cli-inputwrap">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      value={form[f.key as string]}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setField(f.key as string, e.target.value)
                      }
                      placeholder="—"
                    />
                    <span className="al-meas-cli-suffix">{f.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="field">
              <label>Notas</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setField("notes", e.target.value)
                }
                placeholder="Observaciones de la toma (hora, condiciones, etc.)"
              />
            </div>
          </div>
        </div>

        <div className="al-cli-modal-foot">
          <div className="al-cli-modal-foot-left" />
          <div className="al-cli-modal-foot-right">
            <button className="btn btn-ghost" onClick={onClose} disabled={saving} type="button">
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
              type="button"
            >
              {saving ? "Guardando…" : "Guardar medición"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
